"use client";

import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Like useState, but pushes a browser history entry on each state change.
 * When the user presses the browser back button, the state reverts to its
 * previous value. This lets any multi-step UI (login forms, detail views,
 * registration wizards) work correctly with browser navigation.
 *
 * @param key   Unique key to namespace this state in history.state
 * @param initial  The initial value (also the value restored when the user
 *                 navigates all the way back).
 */
export function useHistoryState<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(initial);
  // Track whether we're the ones pushing state (so we don't react to our own push)
  const pushing = useRef(false);

  // On mount, check if history.state already has a value for this key
  // (e.g. user navigated forward to a page they'd been on before)
  useEffect(() => {
    const stored = window.history.state?.[key];
    if (stored !== undefined) {
      setValue(stored);
    }
  }, [key]);

  // Listen for popstate (browser back/forward)
  useEffect(() => {
    function onPopState(e: PopStateEvent) {
      const stored = e.state?.[key];
      setValue(stored !== undefined ? stored : initial);
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [key, initial]);

  const set = useCallback(
    (next: T) => {
      // Don't push if the value hasn't changed
      if (next === value) return;

      pushing.current = true;
      setValue(next);

      // Merge with any existing history state from other useHistoryState hooks
      const merged = { ...window.history.state, [key]: next };
      window.history.pushState(merged, "");
      pushing.current = false;
    },
    [key, value],
  );

  return [value, set];
}
